import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { AxiosResponse } from "axios";
import * as Sentry from "@sentry/react";

import API from "../services/api";

interface FeatureFlagProviderProps {
  children: ReactNode;
}

interface FeatureFlagContextType {
  activeFeatureFlags: string[];
  hasFeatureFlag: (flag: string) => boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextType>({
  activeFeatureFlags: [],
  hasFeatureFlag: (flag) => false,
});

export const useFeatureFlag = () => useContext(FeatureFlagContext);

function FeatureFlagProvider({ children }: FeatureFlagProviderProps) {
  const [activeFeatureFlags, setActiveFeatureFlags] = useState<string[]>([]);

  const hasFeatureFlag = useCallback(
    (flag: string): boolean => activeFeatureFlags.includes(flag),
    [activeFeatureFlags],
  );

  useEffect(() => {
    API.get("/api/feature-flags")
      .then((response: AxiosResponse<any>) => {
        if (response.data) {
          const flags: string[] = [];
          Object.entries(response.data).forEach(([flag, isActive]) => {
            if (isActive) {
              flags.push(flag);
            }
          });
          setActiveFeatureFlags(flags);
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
      });
  }, []);

  return (
    <FeatureFlagContext.Provider value={{ activeFeatureFlags, hasFeatureFlag }}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export default FeatureFlagProvider;
